import './BroadcastsItem.sass';

import React, { FC, useCallback, useMemo } from 'react';
import { Button, List, ListItem } from '$uikit';
import { BroadcastsItemProps } from '$core/BroadcastStats/BroadcastsItem/BroadcastsItem.interface';
import { useDate, useTranslator } from '$hooks';
import { imActions } from '$store/im';
import { useDispatch } from 'react-redux';
import { AttachType, CurrencyEnum } from '$store/models';
import { _confirm, formatCurrency } from '$utils';
import { BroadcastStatsStatusType } from '$store/im/interface';

export const BroadcastsItem: FC<BroadcastsItemProps> = ({ broadcast: b }) => {
  const dispatch = useDispatch();
  const createdAt = useDate(b.createdAt);
  const price = formatCurrency(b.price, CurrencyEnum.EUR);
  const t = useTranslator();

  const handleUnsend = () => {
    _confirm(t('broadcast_stats_unread_confirm'), () =>
      dispatch(imActions.unsendBroadcast(b.id)),
    );
  };

  const attachmentsText = useMemo<string>(() => {
    if (b.attachments.length > 1) {
      return b.attachments.length.toString();
    } else {
      const attach = b.attachments[0];
      if (attach === AttachType.PHOTO) {
        return t('broadcast_stats_attachment_photo');
      } else if (attach === AttachType.VIDEO) {
        return t('broadcast_stats_attachment_video');
      } else {
        return b.attachments.length.toString();
      }
    }
  }, [b]);

  const progress = useMemo(() => {
    switch (b.status) {
      case BroadcastStatsStatusType.STOPPED:
        return t('broadcast_stats_item_progress_stopped');
      case BroadcastStatsStatusType.FINISHED:
        return t('broadcast_stats_item_progress_finished');
      case BroadcastStatsStatusType.PROCESSING:
        return `${b.deliveredCount}/${b.recipientsCount} (${Math.ceil(
          (b.deliveredCount / b.recipientsCount) * 100,
        )}%)`;
      case BroadcastStatsStatusType.ROLLBACK:
        return t('broadcast_stats_item_progress_rollback');
      case BroadcastStatsStatusType.ROLLBACK_FINISHED:
        return t('broadcast_stats_item_progress_rollback_finished');
      case BroadcastStatsStatusType.UNKNOWN:
        return t('broadcast_stats_item_progress_unknown');
      case BroadcastStatsStatusType.OLD:
        return t('broadcast_stats_item_progress_finished');
      default:
        return t('broadcast_stats_item_progress_finished');
    }
  }, [b]);

  return (
    <List className="BroadcastsItem">
      <ListItem indicator={createdAt}>{t('broadcast_stats_item_date')}</ListItem>
      <ListItem indicator={progress}>{t('broadcast_stats_item_progress')}</ListItem>
      <ListItem indicator={b.text}>{t('broadcast_stats_item_text')}</ListItem>
      {!!b.attachments.length && (
        <ListItem indicator={attachmentsText}>
          {t('broadcast_stats_item_attachment')}
        </ListItem>
      )}
      <ListItem indicator={price}>{t('broadcast_stats_item_price')}</ListItem>
      <ListItem indicator={b.recipientsCount.toString()}>
        {t('broadcast_stats_item_sent')}
      </ListItem>
      <ListItem indicator={b.seenCount.toString()}>
        {t('broadcast_stats_item_viewed')}
      </ListItem>
      <ListItem indicator={b.purchasedCount.toString()}>
        {t('broadcast_stats_item_purchased')}
      </ListItem>
      {b.status === BroadcastStatsStatusType.FINISHED && (
        <ListItem
          description={t('broadcast_stats_item_unsend_description')}
          after={
            <Button
              className="BroadcastsItem__unsendButton"
              onClick={handleUnsend}
              color="red"
              mode="inline"
              size="small"
            >
              {t('broadcast_stats_item_unsend_button')}
            </Button>
          }
        >
          {t('broadcast_stats_item_unsend_title')}
        </ListItem>
      )}
    </List>
  );
};
